// Timeline.tsx - Timeline component for video scrubbing and segment visualization

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ComboEdge, ComboRange, Segment, TimelineLanePlacement } from './types';
import {
  getComboDisplayLabel,
  getComboDisplayTitle,
  getComboRange,
  getComboTimelineId,
  getSourceSegments,
  getSourceSegmentDisplayLabel,
  getSourceSegmentDisplayTitle,
  getTimelineSegmentLabelMode,
  isCombo,
  MIN_COMBO_SOURCE_SEGMENTS
} from './utils';
import { formatTimePrecise, formatTimelineWindow } from './helpers';
import { 
  timelineInfoRowStyle,
  timelineTimeWindowStyle,
  getTimelineTickStyle,
  getSourceSegmentTimelineStyle,
  getComboTimelineStyle,
  getComboLaneScrimStyle,
  getComboDividerStyle,
  getComboEdgeHandleStyle,
  getTimelineLaneLabelStyle,
  getComboLaneLabelStyle,
  getTimelineProgressClassStyle,
  getTimelinePlayedTraceStyle,
  getTimelineRulerTickStyle,
  getMarkingSegmentStyle,
  getSegmentPreviewCardStyle,
  getSegmentPreviewCompactTabStyle,
  getSegmentPreviewPointerStyle,
  getTimelineHoverGuideStyle,
  getTimelinePlayheadClassStyle
} from './styles';
import TimelineLanePlacementHandle from './TimelineLanePlacementHandle';

export type TimelineHoverPreviewMode = 'image' | 'compact' | 'none';

type TimelineHoverPreviewStatus = 'idle' | 'loading' | 'image' | 'compact';

type TimelineRulerTickKind = 'minor' | 'mid' | 'major';

type TimelineRulerTick = {
  time: number;
  kind: TimelineRulerTickKind;
  label: string | null;
};

const PREVIEW_VIDEO_EVENT_TIMEOUT_MS = 900;
const isNearStep = (value: number, step: number): boolean => {
  if (step <= 0) return false;
  return Math.abs(value / step - Math.round(value / step)) < 0.001;
};

const getRulerTickConfig = (zoomLevel: number) => {
  if (zoomLevel >= 4) return { minor: 0.25, mid: 1, major: 5 };
  if (zoomLevel >= 3) return { minor: 0.5, mid: 1, major: 5 };
  if (zoomLevel >= 1.5) return { minor: 1, mid: 5, major: 10 };
  return { minor: 1, mid: 5, major: 15 };
};

const formatRulerTime = (time: number): string => {
  const safeTime = Number.isFinite(time) ? Math.max(0, time) : 0;
  const minutes = Math.floor(safeTime / 60);
  const seconds = Math.floor(safeTime % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatHoverGuideTime = (time: number): string => {
  const safeTime = Number.isFinite(time) ? Math.max(0, time) : 0;
  const rounded = Math.round(safeTime * 10) / 10;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded - (minutes * 60);
  return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`;
};

const formatScrubBadgeTime = (time: number): string => {
  const safeTime = Number.isFinite(time) ? Math.max(0, time) : 0;
  const rounded = Math.round(safeTime * 100) / 100;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded - (minutes * 60);
  return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
};

const clamp = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
};

const roundToTenth = (value: number): number => (
  Math.round(value * 10) / 10
);

const getOverlapSecondsForSegment = (
  segment: Pick<Segment, 'startTime' | 'endTime'>,
  overlapSeconds: number
): number => {
  return clamp(overlapSeconds, 0, Math.max(segment.startTime, 0));
};

const formatOverlapPatchTime = (seconds: number): string => (
  `${roundToTenth(Math.max(seconds, 0)).toFixed(1)}s`
);

const getOverlapPatchStyle = (
  segment: Pick<Segment, 'startTime' | 'endTime'>,
  duration: number,
  overlapSeconds: number
): React.CSSProperties => {
  const safeDuration = duration > 0 ? duration : 1;
  const patchStartTime = Math.max(0, segment.startTime - overlapSeconds);
  const patchEndTime = Math.max(0, segment.startTime);
  const leftPercent = (patchStartTime / safeDuration) * 100;
  const widthPercent = Math.max(((patchEndTime - patchStartTime) / safeDuration) * 100, 0);

  return {
    left: `${clamp(leftPercent, 0, 100)}%`,
    width: `${clamp(widthPercent, 0, 100)}%`
  };
};

const compareComboDisplaysBySmallest = (
  a: { segment: Segment; range: ComboRange },
  b: { segment: Segment; range: ComboRange }
): number => (
  a.range.duration - b.range.duration ||
  a.range.sourceSegmentIds.length - b.range.sourceSegmentIds.length ||
  a.range.startIndex - b.range.startIndex ||
  a.range.endIndex - b.range.endIndex ||
  a.segment.name.localeCompare(b.segment.name)
);

const buildRulerTicks = (duration: number, zoomLevel: number): TimelineRulerTick[] => {
  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  if (safeDuration <= 0) return [];

  const { minor, mid, major } = getRulerTickConfig(zoomLevel);
  const ticks: TimelineRulerTick[] = [];
  const totalSteps = Math.floor((safeDuration + 0.001) / minor);

  for (let stepIndex = 0; stepIndex <= totalSteps; stepIndex += 1) {
    const time = Math.min(Math.round(stepIndex * minor * 100) / 100, safeDuration);
    const isLastTick = Math.abs(time - safeDuration) < 0.001;
    const isMajor = time === 0 || isLastTick || isNearStep(time, major);
    const isMid = !isMajor && isNearStep(time, mid);

    ticks.push({
      time,
      kind: isMajor ? 'major' : isMid ? 'mid' : 'minor',
      label: isMajor ? formatRulerTime(time) : null
    });
  }

  const lastTick = ticks[ticks.length - 1];
  if (!lastTick || Math.abs(lastTick.time - safeDuration) >= 0.001) {
    ticks.push({
      time: safeDuration,
      kind: 'major',
      label: formatRulerTime(safeDuration)
    });
  }

  return ticks;
};

interface TimelineProps {
  timelineRef: React.RefObject<HTMLDivElement | null>;
  duration: number;
  currentTime: number;
  segments: Segment[];
  activeSegment: string | null;
  activeComboId?: string | null;
  isMarkingSegment: boolean;
  currentSegmentStart: number | null;
  showComboLane?: boolean;
  showAllCombos?: boolean;
  zoomLevel?: number;
  timelineColor?: string;
  videoSrc?: string | null;
  hoverPreviewMode?: TimelineHoverPreviewMode;
  showTimeWindow?: boolean;
  showProgressLane?: boolean;
  showOverlapPatch?: boolean;
  lanePlacement?: TimelineLanePlacement;
  /** Preroll overlap in seconds. */
  segmentOverlap?: number;
  smoothScrub?: boolean;
  onTimelineClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  onSeek?: (time: number) => void;
  onScrubStart?: (time: number) => void;
  onScrubPreview?: (time: number) => void;
  onScrubEnd?: (time: number) => void;
  onSegmentClick: (segmentId: string) => void;
  onResizeComboEdge?: (comboId: string, edge: ComboEdge, time: number) => void;
  onSegmentOverlapChange?: (overlapSeconds: number) => void;
  onLanePlacementChange?: (placement: TimelineLanePlacement) => void;
}

const Timeline: React.FC<TimelineProps> = ({
  timelineRef,
  duration,
  currentTime,
  segments,
  activeSegment,
  activeComboId = null,
  isMarkingSegment,
  currentSegmentStart,
  showComboLane = true,
  showAllCombos = false,
  zoomLevel = 1,
  timelineColor = 'var(--timeline-playhead-base, #5E4CBA)',
  videoSrc = null,
  hoverPreviewMode = videoSrc ? 'image' : 'compact',
  showTimeWindow = true,
  showProgressLane = true,
  showOverlapPatch = false,
  lanePlacement = 'under-video',
  segmentOverlap = 0,
  smoothScrub = false,
  onTimelineClick,
  onSeek,
  onScrubStart,
  onScrubPreview,
  onScrubEnd,
  onSegmentClick,
  onResizeComboEdge,
  onSegmentOverlapChange,
  onLanePlacementChange
}) => {
  const sourceSegments = useMemo(
    () => getSourceSegments(segments),
    [segments]
  );

  const comboDisplays = useMemo<Array<{ segment: Segment; range: ComboRange }>>(() => {
    const displays = segments.reduce<Array<{ segment: Segment; range: ComboRange }>>((nextDisplays, combo) => {
      if (!isCombo(combo)) return nextDisplays;

      const range = getComboRange(segments, combo);
      if (!range || range.sourceSegmentIds.length < MIN_COMBO_SOURCE_SEGMENTS) return nextDisplays;

      nextDisplays.push({
        segment: {
          ...combo,
          startTime: range.startTime,
          endTime: range.endTime,
          color: range.color,
          sourceSegmentIds: range.sourceSegmentIds,
          originSegmentId: range.originSegmentId
        },
        range
      });
      return nextDisplays;
    }, []);

    return displays
      .sort((a, b) =>
        b.range.duration - a.range.duration ||
        a.range.startTime - b.range.startTime ||
        a.segment.name.localeCompare(b.segment.name)
      );
  }, [segments]);

  const comboTimelineIdBySegmentId = useMemo(
    () => new Map(comboDisplays.map(({ segment, range }) => [segment.id, getComboTimelineId(range)] as const)),
    [comboDisplays]
  );

  const getTimelineSegmentDisplayLabel = useCallback((segment: Segment): string => {
    if (isCombo(segment)) {
      return getComboDisplayLabel(segment, comboTimelineIdBySegmentId.get(segment.id) ?? '');
    }
    return getSourceSegmentDisplayLabel(segments, segment);
  }, [comboTimelineIdBySegmentId, segments]);

  const activeComboDisplay = useMemo(
    () => comboDisplays.find(display => display.segment.id === activeComboId) ?? null,
    [activeComboId, comboDisplays]
  );
  const activeComboRange = activeComboDisplay?.range ?? null;
  const activeSourceSegment = useMemo(
    () => activeSegment
      ? sourceSegments.find(segment => segment.id === activeSegment) ?? null
      : null,
    [activeSegment, sourceSegments]
  );
  const activeSourceComboDisplays = useMemo(
    () => {
      if (!activeSourceSegment) return [];

      const activeSourceIndex = sourceSegments.findIndex(segment => segment.id === activeSourceSegment.id);
      if (activeSourceIndex < 0) return [];

      const relatedDisplays = comboDisplays.filter(display =>
        display.range.sourceSegmentIds.includes(activeSourceSegment.id)
      );
      if (relatedDisplays.length > 0) return relatedDisplays;

      const beforeDisplay = comboDisplays
        .filter(display => display.range.endIndex < activeSourceIndex)
        .sort((a, b) =>
          b.range.endIndex - a.range.endIndex ||
          compareComboDisplaysBySmallest(a, b)
        )[0];
      const afterDisplay = comboDisplays
        .filter(display => display.range.startIndex > activeSourceIndex)
        .sort((a, b) =>
          a.range.startIndex - b.range.startIndex ||
          compareComboDisplaysBySmallest(a, b)
        )[0];

      return [beforeDisplay, afterDisplay]
        .filter((display): display is { segment: Segment; range: ComboRange } => Boolean(display))
        .sort((a, b) => a.range.startIndex - b.range.startIndex || compareComboDisplaysBySmallest(a, b));
    },
    [activeSourceSegment, comboDisplays, sourceSegments]
  );
  const shouldFocusCombosByActiveSource = Boolean(activeSourceSegment);
  const shouldFocusComboLaneByActiveSource = shouldFocusCombosByActiveSource && !showAllCombos;
  const comboLaneDisplays = useMemo(
    () => {
      if (showAllCombos) return comboDisplays;
      if (shouldFocusComboLaneByActiveSource) return activeSourceComboDisplays;
      return activeComboDisplay ? [activeComboDisplay] : [];
    },
    [activeComboDisplay, activeSourceComboDisplays, comboDisplays, shouldFocusComboLaneByActiveSource, showAllCombos]
  );

  const visibleComboSourceIds = useMemo(
    () => new Set(comboLaneDisplays.flatMap(display => display.range.sourceSegmentIds)),
    [comboLaneDisplays]
  );

  const shouldShowComboLane = showComboLane && (
    comboLaneDisplays.length > 0 ||
    (shouldFocusComboLaneByActiveSource && comboDisplays.length > 0)
  );
  const sourceLaneClassName = [
    'timeline-source-lane',
    'timeline-source-track',
    !shouldShowComboLane ? 'is-single-lane' : ''
  ].filter(Boolean).join(' ');
  const markerCount = 5;
  const previewDisplayWidth = 180;
  const previewDisplayHeight = 102;
  const previewCaptureScale = 1.5;
  const previewCaptureMaxWidth = Math.round(previewDisplayWidth * previewCaptureScale);
  const previewCaptureMaxHeight = Math.round(previewDisplayHeight * previewCaptureScale);
  const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(null);
  const [hoverPreviewSrc, setHoverPreviewSrc] = useState<string | null>(null);
  const [hoverPreviewStatus, setHoverPreviewStatus] = useState<TimelineHoverPreviewStatus>('idle');
  const [hoverPreviewLeftPercent, setHoverPreviewLeftPercent] = useState(50);
  const [hoverPreviewAnchorBottomPx, setHoverPreviewAnchorBottomPx] = useState(32);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewCacheRef = useRef<Record<string, string>>({});
  const previewRequestRef = useRef(0);
  const safeDuration = duration > 0 ? duration : 0;
  const timelineZoomLevel = Math.max(1, Number.isFinite(zoomLevel) ? zoomLevel : 1);
  const [timelineWidthPx, setTimelineWidthPx] = useState<number | null>(null);
  useLayoutEffect(() => {
    const timelineElement = timelineRef.current;
    if (!timelineElement) return;

    const syncTimelineWidth = () => {
      const nextWidth = timelineElement.getBoundingClientRect().width;
      if (!Number.isFinite(nextWidth) || nextWidth <= 0) return;

      setTimelineWidthPx((currentWidth) => (
        currentWidth === null || Math.abs(currentWidth - nextWidth) > 0.5
          ? nextWidth
          : currentWidth
      ));
    };

    syncTimelineWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncTimelineWidth);
      return () => window.removeEventListener('resize', syncTimelineWidth);
    }

    const observer = new ResizeObserver(syncTimelineWidth);
    observer.observe(timelineElement);

    return () => observer.disconnect();
  }, [timelineRef, timelineZoomLevel]);
  const rulerTicks = useMemo(
    () => buildRulerTicks(safeDuration, timelineZoomLevel),
    [safeDuration, timelineZoomLevel]
  );
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const [scrubDisplayTime, setScrubDisplayTime] = useState<number | null>(null);
  const [hoverGuide, setHoverGuide] = useState<{ leftPercent: number; time: number } | null>(null);
  const [draggingOverlapSegmentId, setDraggingOverlapSegmentId] = useState<string | null>(null);
  const latestScrubTimeRef = useRef<number | null>(null);
  const isRulerVisible = isDraggingSeek;
  const hoveredComboDisplay = useMemo(
    () => hoveredSegmentId
      ? comboDisplays.find(display => display.segment.id === hoveredSegmentId) ?? null
      : null,
    [comboDisplays, hoveredSegmentId]
  );
  const activePracticeComboDisplay = useMemo(
    () => activeSegment
      ? comboDisplays.find(display => display.segment.id === activeSegment) ?? null
      : null,
    [activeSegment, comboDisplays]
  );
  const emphasizedComboRange = hoveredComboDisplay?.range ?? activePracticeComboDisplay?.range ?? null;
  const overlapTargetSegment = useMemo(() => {
    if (activeSourceSegment) return activeSourceSegment;
    return sourceSegments[Math.floor(sourceSegments.length / 2)] ?? null;
  }, [activeSourceSegment, sourceSegments]);
  const overlapPatchSeconds = overlapTargetSegment
    ? getOverlapSecondsForSegment(overlapTargetSegment, segmentOverlap)
    : 0;
  const shouldRenderOverlapPatch = Boolean(
    showOverlapPatch &&
    overlapTargetSegment &&
    safeDuration > 0 &&
    onSegmentOverlapChange
  );
  const overlapPatchTimeText = formatOverlapPatchTime(overlapPatchSeconds);

  const getTimeFromClientX = useCallback((clientX: number): number | null => {
    if (!Number.isFinite(clientX)) return null;
    if (!timelineRef.current || safeDuration <= 0) return null;

    const rect = timelineRef.current.getBoundingClientRect();
    if (rect.width <= 0) return null;

    const position = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    return position * safeDuration;
  }, [safeDuration, timelineRef]);

  const getTimelinePositionFromClientX = useCallback((clientX: number) => {
    if (!Number.isFinite(clientX)) return null;
    if (!timelineRef.current || safeDuration <= 0) return null;

    const rect = timelineRef.current.getBoundingClientRect();
    if (rect.width <= 0) return null;

    const rawPosition = (clientX - rect.left) / rect.width;
    const position = Math.min(Math.max(rawPosition, 0), 1);
    return {
      leftPercent: Math.round(position * 10000) / 100,
      time: position * safeDuration
    };
  }, [safeDuration, timelineRef]);

  const seekFromClientX = useCallback((
    clientX: number,
    phase: 'start' | 'preview' = 'preview'
  ) => {
    const timelinePosition = getTimelinePositionFromClientX(clientX);
    if (!timelinePosition) return null;

    latestScrubTimeRef.current = timelinePosition.time;
    setScrubDisplayTime(timelinePosition.time);
    setHoverGuide({
      leftPercent: timelinePosition.leftPercent,
      time: Math.round(timelinePosition.time * 10) / 10
    });

    if (smoothScrub) {
      if (phase === 'start') {
        onScrubStart?.(timelinePosition.time);
      } else {
        onScrubPreview?.(timelinePosition.time);
      }
    } else {
      onSeek?.(timelinePosition.time);
    }

    return timelinePosition.time;
  }, [getTimelinePositionFromClientX, onScrubPreview, onScrubStart, onSeek, smoothScrub]);

  const resizeComboEdgeFromClientX = useCallback((
    comboId: string,
    edge: ComboEdge,
    clientX: number
  ) => {
    const time = getTimeFromClientX(clientX);
    if (time === null) return;
    onResizeComboEdge?.(comboId, edge, time);
  }, [getTimeFromClientX, onResizeComboEdge]);

  const updateOverlapFromClientX = useCallback((
    segment: Segment,
    clientX: number
  ) => {
    const time = getTimeFromClientX(clientX);
    if (time === null) return;

    const rawOverlapSeconds = segment.startTime - time;
    const roundedOverlapSeconds = roundToTenth(clamp(rawOverlapSeconds, 0, Math.max(segment.startTime, 0)));
    const nextOverlapSeconds = clamp(roundedOverlapSeconds, 0, Math.max(segment.startTime, 0));

    onSegmentOverlapChange?.(nextOverlapSeconds);
  }, [getTimeFromClientX, onSegmentOverlapChange]);

  const handleOverlapPatchPointerDown = useCallback((
    event: React.PointerEvent<HTMLButtonElement>,
    segment: Segment
  ) => {
    if (!onSegmentOverlapChange) return;

    event.preventDefault();
    event.stopPropagation();
    setDraggingOverlapSegmentId(segment.id);
    updateOverlapFromClientX(segment, event.clientX);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      updateOverlapFromClientX(segment, moveEvent.clientX);
    };

    const handlePointerUp = () => {
      setDraggingOverlapSegmentId(null);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  }, [onSegmentOverlapChange, updateOverlapFromClientX]);

  const handleComboEdgePointerDown = useCallback((
    event: React.PointerEvent<HTMLButtonElement>,
    comboId: string,
    edge: ComboEdge
  ) => {
    if (!onResizeComboEdge) return;

    event.preventDefault();
    event.stopPropagation();
    resizeComboEdgeFromClientX(comboId, edge, event.clientX);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      resizeComboEdgeFromClientX(comboId, edge, moveEvent.clientX);
    };

    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  }, [onResizeComboEdge, resizeComboEdgeFromClientX]);

  const handleSeekKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onSeek || safeDuration <= 0) return;

    const step = event.shiftKey ? 5 : 1;
    let nextTime: number | null = null;

    if (event.key === 'ArrowLeft') {
      nextTime = currentTime - step;
    } else if (event.key === 'ArrowRight') {
      nextTime = currentTime + step;
    } else if (event.key === 'Home') {
      nextTime = 0;
    } else if (event.key === 'End') {
      nextTime = safeDuration;
    }

    if (nextTime === null) return;

    event.preventDefault();
    event.stopPropagation();
    onSeek(Math.min(Math.max(nextTime, 0), safeDuration));
  }, [currentTime, onSeek, safeDuration]);

  useEffect(() => {
    previewRequestRef.current += 1;
    setHoveredSegmentId(null);
    setHoverPreviewSrc(null);
    setHoverPreviewStatus('idle');
    previewCacheRef.current = {};

    if (!videoSrc || hoverPreviewMode !== 'image') {
      previewVideoRef.current = null;
      return;
    }

    const previewVideo = document.createElement('video');
    previewVideo.src = videoSrc;
    previewVideo.preload = 'auto';
    previewVideo.muted = true;
    previewVideo.playsInline = true;
    previewVideo.crossOrigin = 'anonymous';
    previewVideoRef.current = previewVideo;

    return () => {
      previewVideoRef.current = null;
      previewVideo.removeAttribute('src');
    };
  }, [hoverPreviewMode, videoSrc]);

  const clearHoverPreview = useCallback(() => {
    previewRequestRef.current += 1;
    setHoveredSegmentId(null);
    setHoverPreviewSrc(null);
    setHoverPreviewStatus('idle');
  }, []);

  const clearTimelineGuide = useCallback(() => {
    setHoverGuide(null);
  }, []);

  const handleSeekPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if ((!onSeek && !onScrubStart && !onScrubPreview && !onScrubEnd) || safeDuration <= 0) return;

    event.preventDefault();
    event.stopPropagation();
    clearHoverPreview();
    setIsDraggingSeek(true);
    seekFromClientX(event.clientX, 'start');

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      seekFromClientX(moveEvent.clientX, 'preview');
    };

    const handlePointerUp = (upEvent: PointerEvent) => {
      const finalTime = seekFromClientX(upEvent.clientX, 'preview') ?? latestScrubTimeRef.current;
      if (smoothScrub && finalTime !== null) {
        onScrubEnd?.(finalTime);
      }
      latestScrubTimeRef.current = null;
      setScrubDisplayTime(null);
      setIsDraggingSeek(false);
      clearTimelineGuide();
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  }, [
    clearHoverPreview,
    clearTimelineGuide,
    onScrubEnd,
    onScrubPreview,
    onScrubStart,
    onSeek,
    safeDuration,
    seekFromClientX,
    smoothScrub
  ]);

  const handleTimelineRootMouseLeave = useCallback(() => {
    clearHoverPreview();
    if (!isDraggingSeek) {
      clearTimelineGuide();
    }
  }, [clearHoverPreview, clearTimelineGuide, isDraggingSeek]);

  const handleTimelineLanesPointerLeave = useCallback(() => {
    if (!isDraggingSeek) {
      clearTimelineGuide();
    }
  }, [clearTimelineGuide, isDraggingSeek]);

  const handleTimelineLanesPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (draggingOverlapSegmentId) return;
    if (safeDuration <= 0) {
      clearTimelineGuide();
      return;
    }

    const lanesRect = event.currentTarget.getBoundingClientRect();
    const fallbackRect = timelineRef.current?.getBoundingClientRect();
    const rect = lanesRect.width > 0 ? lanesRect : fallbackRect;
    if (!rect || rect.width <= 0) {
      clearTimelineGuide();
      return;
    }

    const rawPosition = (event.clientX - rect.left) / rect.width;
    const position = Math.min(Math.max(rawPosition, 0), 1);
    const time = Math.round(position * safeDuration * 10) / 10;

    setHoverGuide({
      leftPercent: Math.round(position * 10000) / 100,
      time
    });
  }, [clearTimelineGuide, draggingOverlapSegmentId, safeDuration, timelineRef]);

  const capturePreviewFrame = useCallback(async (segmentId: string, requestedTime: number): Promise<string | null> => {
    const previewVideo = previewVideoRef.current;
    if (!previewVideo || !videoSrc) return null;

    const cacheKey = `${segmentId}-${requestedTime.toFixed(2)}`;
    if (previewCacheRef.current[cacheKey]) {
      return previewCacheRef.current[cacheKey];
    }

    const waitForVideoEvent = (eventName: 'loadedmetadata' | 'seeked') => {
      return new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          cleanup();
          reject(new Error(`Preview video ${eventName} timed out`));
        }, PREVIEW_VIDEO_EVENT_TIMEOUT_MS);

        const handleSuccess = () => {
          cleanup();
          resolve();
        };

        const handleError = () => {
          cleanup();
          reject(new Error(`Preview video ${eventName} failed`));
        };

        const cleanup = () => {
          window.clearTimeout(timeoutId);
          previewVideo.removeEventListener(eventName, handleSuccess);
          previewVideo.removeEventListener('error', handleError);
        };

        previewVideo.addEventListener(eventName, handleSuccess, { once: true });
        previewVideo.addEventListener('error', handleError, { once: true });
      });
    };

    try {
      if (previewVideo.readyState < 1) {
        await waitForVideoEvent('loadedmetadata');
      }

      const sourceDuration = Number.isFinite(previewVideo.duration) ? previewVideo.duration : safeDuration;
      const maxAllowedTime = Math.max((sourceDuration || safeDuration) - 0.05, 0);
      const targetTime = Math.min(Math.max(requestedTime, 0), maxAllowedTime);
      previewVideo.currentTime = targetTime;
      await waitForVideoEvent('seeked');

      const sourceWidth = previewVideo.videoWidth || 320;
      const sourceHeight = previewVideo.videoHeight || 180;
      const scale = Math.min(
        previewCaptureMaxWidth / sourceWidth,
        previewCaptureMaxHeight / sourceHeight,
        1
      );
      const frameWidth = Math.max(1, Math.round(sourceWidth * scale));
      const frameHeight = Math.max(1, Math.round(sourceHeight * scale));

      const canvas = document.createElement('canvas');
      canvas.width = frameWidth;
      canvas.height = frameHeight;
      const context = canvas.getContext('2d');
      if (!context) return null;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(previewVideo, 0, 0, frameWidth, frameHeight);
      const previewDataUrl = canvas.toDataURL('image/jpeg', 0.82);
      previewCacheRef.current[cacheKey] = previewDataUrl;
      return previewDataUrl;
    } catch {
      return null;
    }
  }, [previewCaptureMaxHeight, previewCaptureMaxWidth, safeDuration, videoSrc]);

  const handleSegmentMouseEnter = useCallback(async (
    event: React.MouseEvent<HTMLElement>,
    segment: Segment
  ) => {
    const timelineRect = timelineRef.current?.getBoundingClientRect();
    const segmentRect = event.currentTarget.getBoundingClientRect();

    if (timelineRect && timelineRect.width > 0) {
      const centerPx = segmentRect.left + (segmentRect.width / 2) - timelineRect.left;
      const rawPercent = (centerPx / timelineRect.width) * 100;
      const clampedPercent = Math.min(Math.max(rawPercent, 8), 92);
      setHoverPreviewLeftPercent(clampedPercent);
    }
    if (timelineRect && timelineRect.height > 0) {
      const segmentTopPx = Math.min(Math.max(segmentRect.top - timelineRect.top, 0), timelineRect.height);
      setHoverPreviewAnchorBottomPx(timelineRect.height - segmentTopPx);
    }

    setHoveredSegmentId(segment.id);
    setHoverPreviewSrc(null);

    if (hoverPreviewMode === 'none') {
      setHoverPreviewStatus('idle');
      return;
    }

    if (hoverPreviewMode === 'compact' || !videoSrc) {
      setHoverPreviewStatus('compact');
      return;
    }

    setHoverPreviewStatus('loading');
    const segmentPreviewTime = Math.max(segment.startTime, 0);
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    const previewImage = await capturePreviewFrame(segment.id, segmentPreviewTime);

    if (previewRequestRef.current !== requestId) return;

    if (previewImage) {
      setHoverPreviewSrc(previewImage);
      setHoverPreviewStatus('image');
    } else {
      setHoverPreviewStatus('compact');
    }
  }, [capturePreviewFrame, hoverPreviewMode, timelineRef, videoSrc]);

  const hoveredSegment = useMemo(() => {
    if (!hoveredSegmentId) return null;
    return [...sourceSegments, ...comboLaneDisplays.map(display => display.segment)]
      .find((segment) => segment.id === hoveredSegmentId) ?? null;
  }, [comboLaneDisplays, hoveredSegmentId, sourceSegments]);
  const previewCardBottomPx = hoverPreviewAnchorBottomPx + 10;
  const previewPointerBottomPx = hoverPreviewAnchorBottomPx + 2;
  const shouldShowImagePreview = Boolean(hoveredSegment && (
    hoverPreviewStatus === 'loading' ||
    hoverPreviewStatus === 'image'
  ));
  const shouldShowCompactPreview = Boolean(hoveredSegment && hoverPreviewStatus === 'compact');
  const hoveredSegmentDisplayLabel = hoveredSegment ? getTimelineSegmentDisplayLabel(hoveredSegment) : '';
  const displayTime = scrubDisplayTime ?? currentTime;
  const playedTraceStyle = getTimelinePlayedTraceStyle(displayTime, duration, timelineColor);
  const currentPlayheadStyle = getTimelinePlayheadClassStyle(displayTime, duration, timelineColor);
  const shouldRenderSeekHitArea = showProgressLane || smoothScrub;
  const seekHitAreaClassName = [
    'timeline-seek-hit-area',
    showProgressLane ? 'is-progress-lane' : 'is-playhead-only',
    isDraggingSeek ? 'is-dragging' : ''
  ].filter(Boolean).join(' ');
  const playheadClassName = [
    'timeline-playhead',
    shouldShowComboLane ? 'has-combo-lane' : '',
    isDraggingSeek ? 'is-dragging' : ''
  ].filter(Boolean).join(' ');
  const timelineRootClassName = [
    'timeline-inner-track',
    `timeline-lane-placement-${lanePlacement}`,
    showProgressLane ? 'has-progress-lane' : 'no-progress-lane',
    shouldShowComboLane ? 'has-combo-lane' : '',
    isDraggingSeek ? 'is-seeking' : '',
    isRulerVisible ? 'is-ruler-visible' : '',
    hoverGuide ? 'has-hover-guide' : ''
  ].filter(Boolean).join(' ');

  const handleComboKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    comboId: string,
    isDisabled: boolean
  ) => {
    if (isDisabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSegmentClick(comboId);
  };

  // When zoomed in, keep what is playing on screen: the active segment/combo if it fits in the
  // visible window, otherwise the playhead. Manual scrolling pauses this for a few seconds.
  const zoomScrollRef = useRef<HTMLDivElement | null>(null);
  const lastManualTimelineScrollRef = useRef(0);
  useEffect(() => {
    const element = zoomScrollRef.current;
    if (!element) return;
    const markManualScroll = () => {
      lastManualTimelineScrollRef.current = Date.now();
    };
    element.addEventListener('wheel', markManualScroll, { passive: true });
    element.addEventListener('touchstart', markManualScroll, { passive: true });
    element.addEventListener('pointerdown', markManualScroll);
    return () => {
      element.removeEventListener('wheel', markManualScroll);
      element.removeEventListener('touchstart', markManualScroll);
      element.removeEventListener('pointerdown', markManualScroll);
    };
  }, []);

  const followRange = useMemo(() => {
    if (!activeSegment) return null;
    const combo = comboDisplays.find(display => display.segment.id === activeSegment);
    if (combo) return { start: combo.range.startTime, end: combo.range.endTime };
    const source = sourceSegments.find(segment => segment.id === activeSegment);
    return source ? { start: source.startTime, end: source.endTime } : null;
  }, [activeSegment, comboDisplays, sourceSegments]);

  useEffect(() => {
    const element = zoomScrollRef.current;
    if (!element || timelineZoomLevel <= 1 || safeDuration <= 0) return;
    if (Date.now() - lastManualTimelineScrollRef.current < 3000) return;

    const contentWidth = element.scrollWidth;
    const viewWidth = element.clientWidth;
    if (contentWidth <= viewWidth || viewWidth <= 0) return;

    const toPx = (time: number) => (time / safeDuration) * contentWidth;
    const viewStart = element.scrollLeft;
    const viewEnd = viewStart + viewWidth;
    const margin = viewWidth * 0.08;
    let target: number | null = null;

    if (followRange && toPx(followRange.end) - toPx(followRange.start) <= viewWidth - 2 * margin) {
      const rangeStart = toPx(followRange.start);
      const rangeEnd = toPx(followRange.end);
      if (rangeStart < viewStart + margin / 2 || rangeEnd > viewEnd - margin / 2) {
        target = (rangeStart + rangeEnd) / 2 - viewWidth / 2;
      }
    } else {
      const playhead = toPx(currentTime);
      if (playhead < viewStart + margin || playhead > viewEnd - margin) {
        target = playhead - viewWidth * 0.25;
      }
    }

    if (target === null) return;
    const left = Math.max(0, Math.min(contentWidth - viewWidth, target));
    if (Math.abs(left - viewStart) < 1) return;
    if (typeof element.scrollTo === 'function') {
      element.scrollTo({ left, behavior: 'smooth' });
    } else {
      element.scrollLeft = left;
    }
  }, [currentTime, followRange, safeDuration, timelineZoomLevel]);

  return (
    <div
      className={`timeline-shell timeline-lane-placement-${lanePlacement} ${showProgressLane ? 'timeline-has-progress-lane' : 'timeline-no-progress-lane'}`}
      data-testid="timeline-shell"
      data-lane-placement={lanePlacement}
      data-progress-lane={showProgressLane ? 'true' : 'false'}
    >
      {/* Timeline Container */}
      <div
        ref={zoomScrollRef}
        className={`timeline-zoom-scroll ${timelineZoomLevel > 1 ? 'is-zoomed' : ''}`}
        data-testid="timeline-zoom-scroll"
      >
        <div
          ref={timelineRef}
          style={{
            width: `${timelineZoomLevel * 100}%`
          }}
          onClick={onTimelineClick}
          onMouseLeave={handleTimelineRootMouseLeave}
          className={timelineRootClassName}
          data-testid="timeline-root"
          data-lane-placement={lanePlacement}
        >
          <div
            className="timeline-lanes"
            data-testid="timeline-lanes"
            onPointerLeave={handleTimelineLanesPointerLeave}
            onPointerMove={handleTimelineLanesPointerMove}
          >
            <div
              className="timeline-lane-stack"
              data-testid="timeline-lane-stack"
            >
              <TimelineLanePlacementHandle
                placement={lanePlacement}
                onPlacementChange={onLanePlacementChange}
              />
              <div
                className={`timeline-track-stack ${shouldShowComboLane ? 'has-combo-lane' : ''}`}
                data-testid="timeline-segments-layer"
              >
                <div
                  className={sourceLaneClassName}
                  data-testid="timeline-source-lane"
                >
                  <div
                    aria-hidden="true"
                    className="timeline-played-trace timeline-played-trace-lane"
                    data-testid="timeline-played-trace-source"
                    style={playedTraceStyle}
                  />
                  {sourceSegments.map((segment, sourceIndex) => {
                    const isActive = activeSegment === segment.id;
                    const isHovered = hoveredSegmentId === segment.id;
                    const isInActiveCombo = visibleComboSourceIds.has(segment.id);
                    const isSuppressedByCombo = Boolean(
                      emphasizedComboRange?.sourceSegmentIds.includes(segment.id) &&
                      !isActive &&
                      !isHovered
                    );
                    const sourceTimelineId = String(sourceIndex + 1);
                    const sourceDisplayTitle = getSourceSegmentDisplayTitle(segment, sourceTimelineId);
                    const sourceDisplayLabel = getSourceSegmentDisplayLabel(segments, segment);
                    const sourceFullLabel = sourceDisplayTitle !== `Segment ${sourceTimelineId}`
                      ? `${sourceTimelineId} ${sourceDisplayTitle}`
                      : sourceDisplayTitle;
                    const labelMode = isSuppressedByCombo
                      ? 'none'
                      : getTimelineSegmentLabelMode(segment, duration, {
                          kind: 'source',
                          zoomLevel: timelineZoomLevel,
                          timelineWidthPx,
                          numberLabel: sourceTimelineId,
                          nameLabel: sourceFullLabel,
                          isActive,
                          isHovered
                        });
                    const visibleLabelMode = labelMode === 'none' ? 'none' : 'number';
                    const sourceSegmentClassName = [
                      'timeline-source-segment',
                      isActive ? 'is-active' : '',
                      isHovered ? 'is-hovered' : '',
                      isInActiveCombo ? 'is-in-active-combo' : '',
                      isSuppressedByCombo ? 'is-suppressed-by-combo' : '',
                      segment.isDisabled ? 'is-disabled' : ''
                    ].filter(Boolean).join(' ');
                    return (
                      <button
                        type="button"
                        key={`source-${segment.id}`}
                        className={sourceSegmentClassName}
                        data-testid={`timeline-source-segment-${segment.id}`}
                        data-in-active-combo={isInActiveCombo ? 'true' : 'false'}
                        data-label-mode={visibleLabelMode}
                        aria-label={sourceDisplayLabel}
                        title={sourceDisplayTitle}
                        aria-disabled={segment.isDisabled ? 'true' : 'false'}
                        tabIndex={segment.isDisabled ? -1 : 0}
                        style={{
                          ...getSourceSegmentTimelineStyle(
                            segment,
                            duration,
                            isActive,
                            shouldShowComboLane && isInActiveCombo,
                            activeComboRange?.color,
                            isHovered,
                            isSuppressedByCombo
                          ),
                          zIndex: isActive ? 9 : (isInActiveCombo ? 7 : 6)
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (segment.isDisabled) return;
                          onSegmentClick(segment.id);
                        }}
                        onMouseEnter={(e) => {
                          void handleSegmentMouseEnter(e, segment);
                        }}
                        onMouseLeave={() => {
                          clearHoverPreview();
                        }}
                      >
                        {visibleLabelMode !== 'none' && (
                          <span
                            className={`timeline-lane-label timeline-source-label ${isActive || isHovered ? 'is-active' : ''} ${segment.isDisabled ? 'is-disabled' : ''}`}
                            data-testid={`timeline-source-label-${segment.id}`}
                            data-label-mode={visibleLabelMode}
                            style={getTimelineLaneLabelStyle(isActive, Boolean(segment.isDisabled), isHovered && !isActive)}
                          >
                            {sourceTimelineId}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {/* Current segment marker (while marking) - source lane only */}
                  {isMarkingSegment && currentSegmentStart !== null && (
                    <div style={getMarkingSegmentStyle(currentSegmentStart, displayTime, duration)} />
                  )}

                  {shouldRenderOverlapPatch && overlapTargetSegment && (
                    <div
                      className={`timeline-overlap-patch ${draggingOverlapSegmentId === overlapTargetSegment.id ? 'is-dragging' : ''}`}
                      data-testid="timeline-overlap-patch"
                      data-overlap-target-id={overlapTargetSegment.id}
                      data-overlap-seconds={overlapPatchTimeText}
                      style={getOverlapPatchStyle(overlapTargetSegment, safeDuration, overlapPatchSeconds)}
                    >
                      <span
                        className="timeline-overlap-patch-time"
                        data-testid="timeline-overlap-patch-time"
                      >
                        {overlapPatchTimeText}
                      </span>
                      <button
                        type="button"
                        className="timeline-overlap-patch-handle"
                        data-testid="timeline-overlap-patch-handle"
                        aria-label={`Set overlap before ${getTimelineSegmentDisplayLabel(overlapTargetSegment)}`}
                        title="Drag to set overlap"
                        onPointerDown={(event) => handleOverlapPatchPointerDown(event, overlapTargetSegment)}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </div>
                  )}
                </div>

                {shouldShowComboLane && (
                  <div
                    className="timeline-combo-lane timeline-combo-track"
                    data-testid="timeline-combo-lane"
                  >
                    <div
                      aria-hidden="true"
                      className="timeline-played-trace timeline-played-trace-lane"
                      data-testid="timeline-played-trace-combo"
                      style={playedTraceStyle}
                    />
                    {!showAllCombos && !shouldFocusComboLaneByActiveSource && activeComboRange && (
                      <>
                        <div
                          aria-hidden="true"
                          className="timeline-combo-lane-scrim timeline-combo-lane-scrim-left"
                          data-testid="timeline-combo-scrim-left"
                          style={getComboLaneScrimStyle(
                            'left',
                            activeComboRange.startTime,
                            activeComboRange.endTime,
                            duration
                          )}
                        />
                        <div
                          aria-hidden="true"
                          className="timeline-combo-lane-scrim timeline-combo-lane-scrim-right"
                          data-testid="timeline-combo-scrim-right"
                          style={getComboLaneScrimStyle(
                            'right',
                            activeComboRange.startTime,
                            activeComboRange.endTime,
                            duration
                          )}
                        />
                      </>
                    )}
                    {comboLaneDisplays.map(({ segment: comboDisplay, range }, index) => {
                      const isActiveCombo = activeSegment === comboDisplay.id;
                      const isClosestCombo = activeComboId === comboDisplay.id;
                      const isSelectedCombo = !shouldFocusComboLaneByActiveSource && isClosestCombo;
                      const isClosestSourceCombo = shouldFocusComboLaneByActiveSource && isClosestCombo;
                      const isHoveredCombo = hoveredSegmentId === comboDisplay.id;
                      const isEmphasizedCombo = isActiveCombo || isSelectedCombo || isClosestSourceCombo || isHoveredCombo;
                      const comboTimelineId = getComboTimelineId(range);
                      const comboDisplayTitle = getComboDisplayTitle(comboDisplay, comboTimelineId);
                      const comboDisplayLabel = getComboDisplayLabel(comboDisplay, comboTimelineId);
                      const showSeparateComboTitle = comboDisplayTitle !== comboDisplayLabel;
                      const comboFullLabel = showSeparateComboTitle
                        ? `${comboTimelineId} ${comboDisplayTitle}`
                        : comboDisplayTitle;
                      const labelMode = getTimelineSegmentLabelMode(comboDisplay, duration, {
                        kind: 'combo',
                        zoomLevel: timelineZoomLevel,
                        timelineWidthPx,
                        numberLabel: comboTimelineId,
                        nameLabel: comboFullLabel,
                        isActive: isActiveCombo,
                        isHovered: isHoveredCombo,
                        isSelected: isSelectedCombo || isClosestSourceCombo,
                        showAllCombos
                      });
                      const visibleLabelMode = labelMode === 'none' ? 'none' : 'number';
                      const comboZIndex = shouldFocusComboLaneByActiveSource
                        ? 8 + index
                        : isEmphasizedCombo
                        ? 20 + index
                        : showAllCombos
                        ? 8 + index
                        : 8;
                      const comboClassName = [
                        'timeline-active-combo',
                        isActiveCombo ? 'is-active' : '',
                        isSelectedCombo ? 'is-selected' : '',
                        isHoveredCombo ? 'is-hovered' : '',
                        showAllCombos && !isEmphasizedCombo ? 'is-quiet-all-combos' : '',
                        comboDisplay.isDisabled ? 'is-disabled' : ''
                      ].filter(Boolean).join(' ');

                      return (
                        <div
                          key={`combo-${comboDisplay.id}`}
                          className={comboClassName}
                          role="button"
                          tabIndex={comboDisplay.isDisabled ? -1 : 0}
                          aria-label={comboDisplayLabel}
                          title={comboDisplayTitle}
                          aria-disabled={comboDisplay.isDisabled ? 'true' : 'false'}
                          data-testid={`timeline-combo-segment-${comboDisplay.id}`}
                          data-label-mode={visibleLabelMode}
                          style={{
                            ...getComboTimelineStyle(
                              comboDisplay,
                              duration,
                              isActiveCombo,
                              isHoveredCombo,
                              isSelectedCombo || isClosestSourceCombo,
                              showAllCombos
                            ),
                            zIndex: comboZIndex
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (comboDisplay.isDisabled) return;
                            onSegmentClick(comboDisplay.id);
                          }}
                          onKeyDown={(event) => handleComboKeyDown(event, comboDisplay.id, Boolean(comboDisplay.isDisabled))}
                          onMouseEnter={(e) => {
                            void handleSegmentMouseEnter(e, comboDisplay);
                          }}
                          onMouseLeave={() => {
                            clearHoverPreview();
                          }}
                        >
                          {range.sourceSegments.slice(1).map((segment) => (
                            <span
                              key={`combo-divider-${comboDisplay.id}-${segment.id}`}
                              className="timeline-combo-divider"
                              style={getComboDividerStyle(
                                segment.startTime,
                                range.startTime,
                                range.endTime
                              )}
                            />
                          ))}

                          {visibleLabelMode !== 'none' && (
                            <span
                              data-testid={`timeline-combo-label-${comboDisplay.id}`}
                              data-label-mode={visibleLabelMode}
                              className={`timeline-lane-label timeline-combo-label ${isEmphasizedCombo ? 'is-active' : ''} ${comboDisplay.isDisabled ? 'is-disabled' : ''}`}
                              style={getComboLaneLabelStyle(isEmphasizedCombo, Boolean(comboDisplay.isDisabled))}
                            >
                              {comboTimelineId}
                            </span>
                          )}

                          {isSelectedCombo && (
                            <>
                              <button
                                type="button"
                                className={`timeline-combo-handle timeline-combo-handle-left ${isActiveCombo ? 'is-active' : ''}`}
                                data-testid="timeline-combo-handle-left"
                                aria-label={`Resize ${comboDisplayLabel} left edge`}
                                style={getComboEdgeHandleStyle('left', isActiveCombo, comboDisplay.color)}
                                onPointerDown={(event) => handleComboEdgePointerDown(event, comboDisplay.id, 'left')}
                                onClick={(event) => event.stopPropagation()}
                                disabled={comboDisplay.isDisabled || !onResizeComboEdge}
                              >
                                <span className="timeline-combo-handle-bar" />
                              </button>
                              <button
                                type="button"
                                className={`timeline-combo-handle timeline-combo-handle-right ${isActiveCombo ? 'is-active' : ''}`}
                                data-testid="timeline-combo-handle-right"
                                aria-label={`Resize ${comboDisplayLabel} right edge`}
                                style={getComboEdgeHandleStyle('right', isActiveCombo, comboDisplay.color)}
                                onPointerDown={(event) => handleComboEdgePointerDown(event, comboDisplay.id, 'right')}
                                onClick={(event) => event.stopPropagation()}
                                disabled={comboDisplay.isDisabled || !onResizeComboEdge}
                              >
                                <span className="timeline-combo-handle-bar" />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="timeline-transport-layer"
            data-testid="timeline-transport-layer"
          >
            <div
              aria-hidden="true"
              className="timeline-scrub-focus-lane"
              data-testid="timeline-scrub-focus-lane"
            />

            <div
              aria-hidden="true"
              className="timeline-ruler"
              data-testid="timeline-ruler"
            >
              <div
                aria-hidden="true"
                className="timeline-played-trace timeline-played-trace-ruler"
                data-testid="timeline-played-trace-ruler"
                style={playedTraceStyle}
              />
              {rulerTicks.map((tick) => (
                <span
                  key={`ruler-${tick.time}`}
                  className={`timeline-ruler-tick is-${tick.kind}`}
                  data-testid="timeline-ruler-tick"
                  data-tick-kind={tick.kind}
                  style={getTimelineRulerTickStyle(tick.time, safeDuration)}
                >
                  {tick.label && (
                    <span
                      className="timeline-ruler-label"
                      data-testid="timeline-ruler-label"
                    >
                      {tick.label}
                    </span>
                  )}
                </span>
              ))}
            </div>

            {showProgressLane && (
              <div className="timeline-background">
                <div
                  className="timeline-progress"
                  style={getTimelineProgressClassStyle(displayTime, duration, timelineColor)}
                />
                <div className="timeline-tick-layer">
                  {[...Array(markerCount)].map((_, i) => (
                    <span
                      key={`tick-${i}`}
                      className={`timeline-tick ${i === 0 || i === markerCount - 1 ? 'is-edge' : ''}`}
                      style={getTimelineTickStyle(i, markerCount)}
                    />
                  ))}
                </div>
              </div>
            )}

            {shouldRenderSeekHitArea && (
              <div
                role="slider"
                tabIndex={0}
                className={seekHitAreaClassName}
                data-testid="timeline-seek-hit-area"
                data-hit-area-mode={showProgressLane ? 'progress-lane' : 'playhead'}
                aria-label="Playback position"
                aria-valuemin={0}
                aria-valuemax={Number(safeDuration.toFixed(1))}
                aria-valuenow={Number(Math.min(Math.max(displayTime, 0), safeDuration).toFixed(1))}
                title="Drag playback position"
                style={!showProgressLane ? currentPlayheadStyle : undefined}
                onPointerDown={handleSeekPointerDown}
                onKeyDown={handleSeekKeyDown}
                onClick={(event) => event.stopPropagation()}
              />
            )}

            <div
              aria-hidden="true"
              className={`timeline-hover-guide ${hoverGuide ? 'is-active' : ''}`}
              data-testid="timeline-hover-guide"
              style={hoverGuide ? getTimelineHoverGuideStyle(hoverGuide.leftPercent, 'var(--timeline-hover-guide)') : undefined}
            >
              {hoverGuide && (
                <span
                  className="timeline-hover-guide-time"
                  data-testid="timeline-hover-guide-time"
                >
                  {formatHoverGuideTime(hoverGuide.time)}
                </span>
              )}
            </div>

            {/* Playhead */}
            <div aria-hidden="true" className={playheadClassName} style={currentPlayheadStyle}>
              {isDraggingSeek && (
                <span
                  className="timeline-playhead-time-badge"
                  data-testid="timeline-playhead-time-badge"
                >
                  {formatScrubBadgeTime(displayTime)}
                </span>
              )}
              <div className="timeline-playhead-knob">
                <span className="timeline-playhead-grip" />
              </div>
            </div>
          </div>

          {shouldShowImagePreview && hoveredSegment && (
            <>
              <div
                className="timeline-hover-preview-card"
                data-testid="timeline-hover-preview-card"
                style={getSegmentPreviewCardStyle(hoverPreviewLeftPercent, previewCardBottomPx)}
              >
                {hoverPreviewStatus === 'image' && hoverPreviewSrc ? (
                  <img
                    src={hoverPreviewSrc}
                    alt={`${hoveredSegmentDisplayLabel} preview`}
                    className="timeline-hover-preview-image"
                  />
                ) : (
                  <div className="timeline-hover-preview-fallback">
                    Loading preview...
                  </div>
                )}
                <div className="timeline-hover-preview-meta">
                  <div className="timeline-hover-preview-name">{hoveredSegmentDisplayLabel}</div>
                  <div>{`${formatTimePrecise(hoveredSegment.startTime)} - ${formatTimePrecise(hoveredSegment.endTime)}`}</div>
                </div>
              </div>
              <div
                className="timeline-hover-preview-pointer"
                style={getSegmentPreviewPointerStyle(hoverPreviewLeftPercent, previewPointerBottomPx)}
              />
            </>
          )}

          {shouldShowCompactPreview && hoveredSegment && (
            <>
              <div
                className="timeline-hover-preview-compact-tab"
                data-testid="timeline-hover-preview-compact-tab"
                style={getSegmentPreviewCompactTabStyle(hoverPreviewLeftPercent, previewCardBottomPx)}
              >
                {hoveredSegmentDisplayLabel}
              </div>
              <div
                className="timeline-hover-preview-pointer"
                style={getSegmentPreviewPointerStyle(hoverPreviewLeftPercent, previewPointerBottomPx)}
              />
            </>
          )}
        </div>
      </div>

      {showTimeWindow && (
        <div style={timelineInfoRowStyle}>
          <span style={timelineTimeWindowStyle}>
            {formatTimelineWindow(displayTime, duration)}
          </span>
        </div>
      )}
    </div>
  );
};

export default Timeline;
